/**
 * useRazorpayPayment Hook
 * 
 * React hook for handling Razorpay payment flow:
 * 1. Dynamically loads Razorpay checkout script
 * 2. Calls backend /payment/create-order
 * 3. Opens Razorpay payment modal
 * 4. On success → calls /payment/verify
 * 5. Handles payment failure and cancellation
 * 
 * Usage:
 * const { initiatePayment, loading, error } = useRazorpayPayment();
 * 
 * const handlePayment = async () => {
 *   const result = await initiatePayment({
 *     planType: "SINGLE_INTERVIEW",
 *     userEmail: "user@example.com",
 *     userName: "John Doe",
 *     userPhone: "9876543210",
 *     onSuccess: (response) => {
 *       console.log("Payment successful!", response);
 *       // Navigate to dashboard
 *     },
 *     onFailure: (error) => {
 *       console.error("Payment failed", error);
 *     }
 *   });
 * };
 */

import { useState, useCallback, useRef } from 'react';
import { createRazorpayOrder, verifyPayment } from '../api';

// Razorpay script URL
const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Load Razorpay checkout script dynamically
 * @returns {Promise<boolean>} - True if loaded successfully
 */
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    // Check if script already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    // Check if script element already exists
    const existingScript = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existingScript) {
      existingScript.onload = () => resolve(true);
      existingScript.onerror = () => resolve(false);
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      console.log('✅ Razorpay script loaded');
      resolve(true);
    };
    script.onerror = () => {
      console.error('❌ Failed to load Razorpay script');
      resolve(false);
    };

    document.body.appendChild(script);
  });
};

/**
 * Custom hook for Razorpay payment integration
 */
export const useRazorpayPayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const razorpayInstanceRef = useRef(null);

  /**
   * Initiate Razorpay payment flow
   * 
   * @param {Object} options - Payment options
   * @param {string} options.planType - Plan type (e.g., "SINGLE_INTERVIEW")
   * @param {string} options.userEmail - User email for prefill
   * @param {string} [options.userName] - User name for prefill (optional)
   * @param {string} [options.userPhone] - User phone for prefill (optional)
   * @param {string} [options.discountCode] - Discount/coupon code (optional)
   * @param {Function} [options.onSuccess] - Success callback
   * @param {Function} [options.onFailure] - Failure callback
   * @param {Function} [options.onDismiss] - Modal dismissed callback
   * 
   * @returns {Promise<Object|null>} - Payment verification result or null on failure
   */
  const initiatePayment = useCallback(async (options) => {
    const {
      planType,
      userEmail,
      userName,
      userPhone,
      discountCode,
      trialOffer,
      onSuccess,
      onFailure,
      onDismiss
    } = options;

    setLoading(true);
    setError(null);

    try {
      // ============ STEP 1: LOAD RAZORPAY SCRIPT ============
      console.log('📦 Loading Razorpay script...');
      const isScriptLoaded = await loadRazorpayScript();

      if (!isScriptLoaded) {
        const errorMsg = 'Failed to load Razorpay checkout script';
        setError(errorMsg);
        setLoading(false);
        if (onFailure) onFailure({ error: errorMsg, code: 'SCRIPT_LOAD_FAILED' });
        return null;
      }

      // ============ STEP 2: CREATE PAYMENT ORDER ============
      console.log(`💳 Creating payment order for plan: ${planType}`, discountCode ? `with coupon: ${discountCode}` : '');
      const orderResponse = await createRazorpayOrder(planType, discountCode, trialOffer);

      if (!orderResponse || orderResponse.status !== 'success') {
        const errorMsg = orderResponse?.message || 'Failed to create payment order';
        setError(errorMsg);
        setLoading(false);
        if (onFailure) onFailure({ error: errorMsg, code: orderResponse?.error_code || 'ORDER_CREATION_FAILED' });
        return null;
      }

      // ============ CHECK IF FREE ACTIVATION ============
      if (orderResponse.free_activation) {
        console.log('🎉 FREE ACTIVATION! No payment required, activating entitlement...');
        setLoading(false);
        
        // Call success callback with activation details
        if (onSuccess) {
          onSuccess({
            free_activation: true,
            payment_reference: orderResponse.payment_reference,
            coupon_code: orderResponse.coupon_code,
            plan_type: orderResponse.plan_type,
            redirect_to: orderResponse.redirect_to,
            entitlement: orderResponse.entitlement
          });
        }
        
        return {
          success: true,
          free_activation: true,
          redirect_to: orderResponse.redirect_to
        };
      }

      // ============ CHECK IF MOCK ORDER (TEST MODE) ============
      if (orderResponse.is_mock_order) {
        console.log('⚠️ MOCK ORDER detected - Test mode (Razorpay not configured)');
        console.log('💡 In production, Razorpay payment would be processed here.');
        
        const errorMsg = orderResponse.warning || 'Payment gateway not configured. This is a test order.';
        setError(errorMsg);
        setLoading(false);
        
        if (onFailure) {
          onFailure({ 
            error: errorMsg, 
            code: 'MOCK_ORDER',
            details: 'Razorpay is not configured on the backend. Configure RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET environment variables to enable real payments.'
          });
        }
        
        return {
          success: false,
          is_mock_order: true,
          message: errorMsg
        };
      }

      const {
        order_id,
        amount,
        currency,
        razorpay_key_id,
        plan_type
      } = orderResponse;

      console.log(`✅ Order created: ${order_id}, Amount: ₹${amount / 100}`);

      // ============ STEP 3: OPEN RAZORPAY CHECKOUT ============
      const razorpayOptions = {
        key: razorpay_key_id,
        amount: amount, // Amount in paise
        currency: currency,
        order_id: order_id,
        name: 'Vagmee',
        description: `${plan_type} - AI Interview Practice`,
        image: '/Vagmee_Logo.png', // Your logo
        prefill: {
          email: userEmail,
          name: userName || '',
          contact: userPhone || ''
        },
        theme: {
          color: '#4F46E5' // Indigo color
        },
        config: {
          display: {
            hide: [
              // Don't hide any payment methods - show all enabled in dashboard
            ],
            preferences: {
              show_default_blocks: true // Show all available payment methods
            }
          }
        },

        // ============ SUCCESS HANDLER ============
        handler: async function (response) {
          console.log('✅ Payment successful, verifying...', response);

          try {
            // Verify payment with backend
            const verifyResponse = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_type: plan_type
            });

            if (verifyResponse && verifyResponse.success) {
              console.log('✅ Payment verified successfully!', verifyResponse);
              setLoading(false);
              if (onSuccess) onSuccess(verifyResponse);
              return verifyResponse;
            } else {
              const errorMsg = verifyResponse?.message || 'Payment verification failed';
              console.error('❌ Verification failed:', errorMsg);
              setError(errorMsg);
              setLoading(false);
              if (onFailure) onFailure({ error: errorMsg, code: 'VERIFICATION_FAILED' });
              return null;
            }
          } catch (verifyError) {
            console.error('❌ Verification error:', verifyError);
            const errorMsg = verifyError.message || 'Failed to verify payment';
            setError(errorMsg);
            setLoading(false);
            if (onFailure) onFailure({ error: errorMsg, code: 'VERIFICATION_ERROR' });
            return null;
          }
        },

        // ============ MODAL DISMISSED HANDLER ============
        modal: {
          ondismiss: function () {
            console.log('⚠️ Payment modal dismissed by user');
            setLoading(false);
            if (onDismiss) {
              onDismiss();
            } else if (onFailure) {
              onFailure({ error: 'Payment cancelled by user', code: 'PAYMENT_CANCELLED' });
            }
          }
        }
      };

      // Create and open Razorpay checkout
      console.log('🚀 Opening Razorpay checkout...');
      const razorpayInstance = new window.Razorpay(razorpayOptions);
      razorpayInstanceRef.current = razorpayInstance;

      // Handle payment failure
      razorpayInstance.on('payment.failed', function (response) {
        console.error('❌ Payment failed:', response.error);
        const errorMsg = response.error.description || 'Payment failed';
        setError(errorMsg);
        setLoading(false);
        if (onFailure) {
          onFailure({
            error: errorMsg,
            code: response.error.code,
            reason: response.error.reason,
            metadata: response.error.metadata
          });
        }
      });

      // Open the modal
      razorpayInstance.open();

    } catch (err) {
      console.error('❌ Payment initiation error:', err);
      const errorMsg = err.message || 'An error occurred while initiating payment';
      setError(errorMsg);
      setLoading(false);
      if (onFailure) onFailure({ error: errorMsg, code: 'INITIATION_ERROR' });
      return null;
    }

    // Note: Loading state will be cleared by success/failure handlers
    return null;
  }, []);

  /**
   * Close Razorpay modal if open
   */
  const closePaymentModal = useCallback(() => {
    if (razorpayInstanceRef.current) {
      razorpayInstanceRef.current.close();
      razorpayInstanceRef.current = null;
      setLoading(false);
      console.log('🔒 Razorpay modal closed');
    }
  }, []);

  return {
    initiatePayment,
    closePaymentModal,
    loading,
    error,
    setError
  };
};

export default useRazorpayPayment;
